/**
 * Creazione nuovo preventivo di acquisto — modulo completo.
 *
 * Flusso:
 * 1. Seleziona fornitore → mostra contatti e dati fiscali
 * 2. Compila condizioni (scadenza, pagamento, consegna, spedizione, minimo ordine)
 * 3. Cerca articoli nel catalogo fornitore e aggiungi con quantità
 * 4. Riepilogo → crea preventivo → redirect al dettaglio
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Combobox, type ComboboxOption, Input, Label } from '@floraltech/ui';
import { SoggettoQuickCreateDialog, type SoggettoCreato } from '../components/SoggettoQuickCreateDialog';

interface Fornitore {
  id: string;
  ragione_sociale: string | null;
  nome: string | null;
  cognome: string | null;
  email: string | null;
  pec: string | null;
  telefono: string | null;
  partita_iva: string | null;
}

interface CatalogoItem {
  id: string;
  sku: string;
  descrizione: string | null;
  unita_misura_id: string | null;
  prezzo_unitario: string;
  moq: string;
}

interface RigaDaAggiungere {
  catalogo_id: string;
  sku: string;
  descrizione: string;
  unita_misura: string | null;
  quantita: string;
}

type QtyMap = Record<string, string>;

const PLACEHOLDER_PAGAMENTO = 'es. 30gg DFFM, bonifico anticipato, ecc.';

export function NuovoPreventivo() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  // Step 1: fornitore
  const [fornitori, setFornitori] = useState<Fornitore[]>([]);
  const [fornitoreId, setFornitoreId] = useState('');
  const [fornitoreSelezionato, setFornitoreSelezionato] = useState<Fornitore | null>(null);
  const [fornitoreSearch, setFornitoreSearch] = useState('');
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);

  // Step 2: condizioni
  const [dataValidita, setDataValidita] = useState('');
  const [terminiPagamento, setTerminiPagamento] = useState('');
  const [tempoConsegnaGiorni, setTempoConsegnaGiorni] = useState('');
  const [speseSpedizione, setSpeseSpedizione] = useState('');
  const [minimoOrdine, setMinimoOrdine] = useState('');
  const [note, setNote] = useState('');

  // Step 3: catalogo fornitore
  const [catalogo, setCatalogo] = useState<CatalogoItem[]>([]);
  const [catalogoSearch, setCatalogoSearch] = useState('');
  const [loadingCatalogo, setLoadingCatalogo] = useState(false);
  const [catalogoQty, setCatalogoQty] = useState<QtyMap>({});

  // Step 4: righe selezionate
  const [righe, setRighe] = useState<RigaDaAggiungere[]>([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchFornitori = useCallback(async () => {
    const res = await fetch('/api/v1/soggetti?tipo=fornitore&limit=200', {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      const data = await res.json();
      setFornitori(Array.isArray(data) ? data : data.items ?? []);
    }
  }, [token]);

  useEffect(() => {
    fetchFornitori();
  }, [fetchFornitori]);

  /** Fornitore creato al volo dal picker: ricarica la lista (dati completi) e lo seleziona. */
  const handleFornitoreCreato = useCallback(
    async (creato: SoggettoCreato) => {
      await fetchFornitori();
      setFornitoreSearch('');
      setFornitoreId(creato.id);
    },
    [fetchFornitori],
  );

  // Aggiorna fornitore selezionato
  useEffect(() => {
    if (!fornitoreId) {
      setFornitoreSelezionato(null);
      return;
    }
    const f = fornitori.find((f) => f.id === fornitoreId) ?? null;
    setFornitoreSelezionato(f);
  }, [fornitoreId, fornitori]);

  // Carica catalogo quando cambia fornitore
  useEffect(() => {
    if (!fornitoreId) {
      setCatalogo([]);
      return;
    }
    const fetchCatalogo = async () => {
      setLoadingCatalogo(true);
      const params = new URLSearchParams({ limit: '200' });
      if (catalogoSearch) params.set('q', catalogoSearch);
      const res = await fetch(
        `/api/v1/fornitori/${fornitoreId}/catalogo?${params}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.ok) setCatalogo(await res.json());
      setLoadingCatalogo(false);
    };
    fetchCatalogo();
  }, [fornitoreId, catalogoSearch, token]);

  const nomeFornitore = (f: Fornitore): string =>
    f.ragione_sociale || [f.nome, f.cognome].filter(Boolean).join(' ') || '—';

  const isRigaAggiunta = (catalogoId: string) =>
    righe.some((r) => r.catalogo_id === catalogoId);

  const aggiungiRiga = (item: CatalogoItem) => {
    if (isRigaAggiunta(item.id)) return;
    const qty = catalogoQty[item.id] || '1';
    if (!qty || parseFloat(qty) <= 0) return;
    setRighe((prev) => [
      ...prev,
      {
        catalogo_id: item.id,
        sku: item.sku,
        descrizione: item.descrizione || item.sku,
        unita_misura: null,
        quantita: qty,
      },
    ]);
  };

  const rimuoviRiga = (catalogoId: string) => {
    setRighe((prev) => prev.filter((r) => r.catalogo_id !== catalogoId));
  };

  const updateQuantita = (catalogoId: string, quantita: string) => {
    setRighe((prev) =>
      prev.map((r) =>
        r.catalogo_id === catalogoId ? { ...r, quantita } : r,
      ),
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fornitoreId) {
      setError('Seleziona un fornitore');
      return;
    }
    if (righe.length === 0) {
      setError('Aggiungi almeno un articolo');
      return;
    }

    setLoading(true);
    setError('');

    const body: Record<string, unknown> = { fornitore_id: fornitoreId };
    if (dataValidita) body.data_validita = dataValidita;
    if (note.trim()) body.note = note.trim();
    if (terminiPagamento.trim()) body.termini_pagamento = terminiPagamento.trim();
    if (tempoConsegnaGiorni) body.tempo_consegna_giorni = parseInt(tempoConsegnaGiorni, 10);
    if (speseSpedizione) body.spese_spedizione = speseSpedizione;
    if (minimoOrdine) body.minimo_ordine = minimoOrdine;

    const prevRes = await fetch('/api/v1/preventivi', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    if (!prevRes.ok) {
      const detail = await prevRes.json().catch(() => null);
      setError(detail?.detail || `Errore ${prevRes.status}`);
      setLoading(false);
      return;
    }

    const preventivo = await prevRes.json();

    for (const riga of righe) {
      await fetch(`/api/v1/preventivi/${preventivo.id}/righe`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          descrizione: riga.descrizione,
          quantita: riga.quantita,
          codice_articolo: riga.sku,
          confermata: true,
        }),
      });
    }

    navigate(`/preventivi/${preventivo.id}`);
  };

  return (
    <div className="p-6 w-full space-y-6">
      <h1 className="text-xl font-outfit font-semibold text-foreground">
        Nuovo preventivo
      </h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Step 1: Fornitore */}
        <Card className="p-4 space-y-4">
          <h2 className="text-sm font-semibold text-foreground">1. Fornitore</h2>
          <div className="space-y-1">
            <Label>Fornitore *</Label>
            <Combobox
              value={fornitoreSelezionato ? nomeFornitore(fornitoreSelezionato) : fornitoreSearch}
              onInputChange={(v) => {
                setFornitoreSearch(v);
                if (fornitoreSelezionato && v !== nomeFornitore(fornitoreSelezionato)) {
                  setFornitoreId('');
                  setFornitoreSelezionato(null);
                  setRighe([]);
                  setCatalogoQty({});
                }
              }}
              onSelect={(opt) => {
                setFornitoreId(opt.value);
                setFornitoreSearch('');
                setRighe([]);
                setCatalogoSearch('');
                setCatalogoQty({});
              }}
              options={fornitori
                .filter((f) => {
                  if (!fornitoreSearch) return true;
                  const q = fornitoreSearch.toLowerCase();
                  return (
                    nomeFornitore(f).toLowerCase().includes(q) ||
                    (f.partita_iva || '').toLowerCase().includes(q)
                  );
                })
                .map<ComboboxOption>((f) => ({
                  value: f.id,
                  label: nomeFornitore(f),
                  sublabel: f.partita_iva ? `P.IVA ${f.partita_iva}` : (f.email || ''),
                }))}
              placeholder="Cerca fornitore per nome o P.IVA..."
              onCreateNew={() => setQuickCreateOpen(true)}
              createNewLabel="Crea nuovo fornitore..."
            />
          </div>

          {/* Info fornitore selezionato */}
          {fornitoreSelezionato && (
            <div className="grid grid-cols-2 gap-3 text-sm bg-muted/50 rounded p-3">
              {fornitoreSelezionato.partita_iva && (
                <div>
                  <span className="text-muted-foreground">P.IVA:</span>{' '}
                  <span className="font-mono">{fornitoreSelezionato.partita_iva}</span>
                </div>
              )}
              {fornitoreSelezionato.email && (
                <div>
                  <span className="text-muted-foreground">Email:</span>{' '}
                  {fornitoreSelezionato.email}
                </div>
              )}
              {fornitoreSelezionato.pec && (
                <div>
                  <span className="text-muted-foreground">PEC:</span>{' '}
                  {fornitoreSelezionato.pec}
                </div>
              )}
              {fornitoreSelezionato.telefono && (
                <div>
                  <span className="text-muted-foreground">Tel:</span>{' '}
                  {fornitoreSelezionato.telefono}
                </div>
              )}
              {!fornitoreSelezionato.email && !fornitoreSelezionato.pec && (
                <div className="col-span-2 text-amber-600 dark:text-amber-400 text-xs">
                  Nessun indirizzo email/PEC — l'invio automatico non sarà possibile
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Step 2: Condizioni */}
        {fornitoreId && (
          <Card className="p-4 space-y-4">
            <h2 className="text-sm font-semibold text-foreground">2. Condizioni</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="data_validita">Risposta entro</Label>
                <Input
                  id="data_validita"
                  type="date"
                  value={dataValidita}
                  onChange={(e) => setDataValidita(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="tempo_consegna">Tempo consegna (giorni)</Label>
                <Input
                  id="tempo_consegna"
                  type="number"
                  min="1"
                  step="1"
                  value={tempoConsegnaGiorni}
                  onChange={(e) => setTempoConsegnaGiorni(e.target.value)}
                  className="mt-1"
                  placeholder="es. 7"
                />
              </div>
              <div>
                <Label htmlFor="termini_pagamento">Termini di pagamento</Label>
                <Input
                  id="termini_pagamento"
                  value={terminiPagamento}
                  onChange={(e) => setTerminiPagamento(e.target.value)}
                  className="mt-1"
                  placeholder={PLACEHOLDER_PAGAMENTO}
                />
              </div>
              <div>
                <Label htmlFor="spese_spedizione">Spese spedizione</Label>
                <Input
                  id="spese_spedizione"
                  type="number"
                  min="0"
                  step="0.01"
                  value={speseSpedizione}
                  onChange={(e) => setSpeseSpedizione(e.target.value)}
                  className="mt-1"
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label htmlFor="minimo_ordine">Minimo ordine</Label>
                <Input
                  id="minimo_ordine"
                  type="number"
                  min="0"
                  step="0.01"
                  value={minimoOrdine}
                  onChange={(e) => setMinimoOrdine(e.target.value)}
                  className="mt-1"
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label htmlFor="note">Note</Label>
                <Input
                  id="note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  className="mt-1"
                  placeholder="Annotazioni aggiuntive..."
                />
              </div>
            </div>
          </Card>
        )}

        {/* Step 3: Catalogo fornitore */}
        {fornitoreId && (
          <Card className="p-4 space-y-3">
            <h2 className="text-sm font-semibold text-foreground">
              3. Articoli dal catalogo fornitore
            </h2>
            <Input
              placeholder="Cerca per SKU o descrizione..."
              value={catalogoSearch}
              onChange={(e) => setCatalogoSearch(e.target.value)}
            />
            {loadingCatalogo ? (
              <p className="text-sm text-muted-foreground">Caricamento catalogo...</p>
            ) : catalogo.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Nessun articolo nel catalogo di questo fornitore
              </p>
            ) : (
              <div className="max-h-72 overflow-y-auto border border-border rounded">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-muted/80 backdrop-blur">
                    <tr className="border-b border-border">
                      <th className="text-left px-3 py-1.5 text-muted-foreground font-medium">SKU</th>
                      <th className="text-left px-3 py-1.5 text-muted-foreground font-medium">Descrizione</th>
                      <th className="text-right px-3 py-1.5 text-muted-foreground font-medium w-24">Q.ta</th>
                      <th className="px-3 py-1.5 w-24"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {catalogo.map((item) => {
                      const added = isRigaAggiunta(item.id);
                      return (
                        <tr
                          key={item.id}
                          className={`border-b border-border ${added ? 'bg-green-50 dark:bg-green-900/10' : 'hover:bg-muted/30'}`}
                        >
                          <td className="px-3 py-1.5 font-mono text-xs">{item.sku}</td>
                          <td className="px-3 py-1.5">{item.descrizione || '—'}</td>
                          <td className="px-3 py-1.5">
                            {!added && (
                              <Input
                                type="number"
                                min="1"
                                step="1"
                                value={catalogoQty[item.id] ?? ''}
                                onChange={(e) =>
                                  setCatalogoQty((prev) => ({ ...prev, [item.id]: e.target.value }))
                                }
                                placeholder="Q.ta"
                                className="w-20 text-right text-sm ml-auto"
                              />
                            )}
                          </td>
                          <td className="px-3 py-1.5 text-center">
                            {added ? (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => rimuoviRiga(item.id)}
                                className="text-destructive text-xs"
                              >
                                Rimuovi
                              </Button>
                            ) : (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => aggiungiRiga(item)}
                                disabled={!catalogoQty[item.id] || parseFloat(catalogoQty[item.id]) <= 0}
                                className="text-xs"
                              >
                                Aggiungi
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        )}

        {/* Step 4: Riepilogo articoli selezionati */}
        {righe.length > 0 && (
          <Card className="p-4 space-y-3">
            <h2 className="text-sm font-semibold text-foreground">
              4. Riepilogo ({righe.length} articol{righe.length === 1 ? 'o' : 'i'})
            </h2>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left px-3 py-1.5 text-muted-foreground font-medium">SKU</th>
                  <th className="text-left px-3 py-1.5 text-muted-foreground font-medium">Descrizione</th>
                  <th className="text-right px-3 py-1.5 text-muted-foreground font-medium w-28">Q.ta</th>
                  <th className="px-3 py-1.5 w-20"></th>
                </tr>
              </thead>
              <tbody>
                {righe.map((r) => (
                  <tr key={r.catalogo_id} className="border-b border-border">
                    <td className="px-3 py-1.5 font-mono text-xs">{r.sku}</td>
                    <td className="px-3 py-1.5">{r.descrizione}</td>
                    <td className="px-3 py-1.5">
                      <Input
                        type="number"
                        min="1"
                        step="1"
                        value={r.quantita}
                        onChange={(e) => updateQuantita(r.catalogo_id, e.target.value)}
                        className="text-right w-24 ml-auto"
                      />
                    </td>
                    <td className="px-3 py-1.5 text-center">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => rimuoviRiga(r.catalogo_id)}
                        className="text-destructive text-xs"
                      >
                        Rimuovi
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}

        {error && <p className="text-sm text-destructive">{error}</p>}

        <div className="flex gap-2 justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(-1)}
          >
            Annulla
          </Button>
          <Button type="submit" disabled={loading || !fornitoreId || righe.length === 0}>
            {loading ? 'Creazione...' : 'Crea preventivo'}
          </Button>
        </div>
      </form>

      <SoggettoQuickCreateDialog
        open={quickCreateOpen}
        onOpenChange={setQuickCreateOpen}
        tipo="fornitore"
        presetNome={fornitoreSearch}
        onCreated={handleFornitoreCreato}
      />
    </div>
  );
}
